import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, ChevronLeft, Sprout, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

import { ConfirmButton } from "../components/ConfirmButton";
import { ErrorBanner } from "../components/ErrorBanner";
import { SectionLabel } from "../components/SectionLabel";
import { Skeleton } from "../components/Skeleton";
import { TrayIcon } from "../components/TrayIcon";
import {
  api,
  ApiRequestError,
  formatTrayFraction,
  TRAY_FRACTION_VALUES,
  type DeliveryTarget,
  type GeneratorWarning,
  type IsoDate,
  type PlanAlreadyAppliedCycle,
  type PlanApplyConflict,
  type PlanGenerateBody,
  type PlanGenerateResponse,
  type PlanRow,
  type PlanTrayInput,
  type RecommendedEvent,
  type ReferenceRow,
  type TrayFraction,
} from "../lib/api";
import { addDays, todayISO } from "../lib/date";
import { useReference } from "../lib/queries";
import { colors, kindStyles, TRAY_IDS, trayColors, typography, type TrayId } from "../styles/theme";

type WizardStep = "name" | "date" | "trays" | "preview";

type TrayDraft = {
  tray_id: TrayId;
  crop: string;
  delivery_target: DeliveryTarget;
  tray_fraction: TrayFraction;
  seed_oz_override?: string;
};

const DEFAULT_CROPS: Record<TrayId, string> = {
  A: "Pea shoots",
  B: "Sunflower (black oil)",
  C: "Radish (confetti mix)",
  D: "Brookfield Brassica Mix",
};

const DEFAULT_TARGETS: Record<TrayId, DeliveryTarget> = {
  A: "friday",
  B: "friday",
  C: "tuesday",
  D: "friday",
};

function usePlans() {
  return useQuery({ queryKey: ["plans"], queryFn: () => api.listPlans() });
}

type ApplyConflict =
  | { kind: "tray_conflict"; conflicts: PlanApplyConflict[] }
  | { kind: "plan_already_applied"; cycles: PlanAlreadyAppliedCycle[] };

// Parses 409 responses from /api/plans/:id/apply into a structured conflict
// or rethrows. Wizard + PlanRowCard share the same shape so the resolution
// UX (delete-and-reapply, end-active-cycles) stays consistent across surfaces.
function parseApplyConflict(err: unknown): ApplyConflict | null {
  if (!(err instanceof ApiRequestError) || err.status !== 409) return null;
  const code = err.body?.error;
  const details = (err.body?.details ?? {}) as {
    conflicts?: PlanApplyConflict[];
    cycles?: PlanAlreadyAppliedCycle[];
  };
  if (code === "tray_conflict") {
    return { kind: "tray_conflict", conflicts: details.conflicts ?? [] };
  }
  if (code === "plan_already_applied") {
    return { kind: "plan_already_applied", cycles: details.cycles ?? [] };
  }
  return null;
}

function defaultTrays(): TrayDraft[] {
  return TRAY_IDS.map((id) => ({
    tray_id: id,
    crop: DEFAULT_CROPS[id],
    delivery_target: DEFAULT_TARGETS[id],
    tray_fraction: 1,
  }));
}

function addIsoDays(iso: IsoDate, n: number): IsoDate {
  return addDays(iso, n);
}

function weekBucket(isoDate: IsoDate, startDate: IsoDate): number {
  const a = new Date(`${isoDate}T00:00:00Z`).getTime();
  const b = new Date(`${startDate}T00:00:00Z`).getTime();
  return Math.floor((a - b) / (7 * 24 * 60 * 60 * 1000));
}

export function PlansView({ onNavigateToday }: { onNavigateToday: () => void }) {
  const plans = usePlans();
  const [mode, setMode] = useState<"list" | "wizard">("list");
  const [activePlan, setActivePlan] = useState<PlanRow | null>(null);

  if (mode === "wizard") {
    return (
      <GeneratorWizard
        existingPlan={activePlan}
        onExit={() => {
          setMode("list");
          setActivePlan(null);
        }}
        onNavigateToday={() => {
          setMode("list");
          setActivePlan(null);
          onNavigateToday();
        }}
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <SectionLabel>SAVED PLANS</SectionLabel>
          <button
            type="button"
            onClick={() => setMode("wizard")}
            style={{
              minHeight: "44px",
              padding: "0 16px",
              background: colors.infrared,
              color: "#fff",
              border: "none",
              fontFamily: typography.mono,
              fontSize: "11px",
              letterSpacing: "0.15em",
              cursor: "pointer",
            }}
          >
            NEW PLAN
          </button>
        </div>
        <div
          style={{ fontSize: "12px", lineHeight: 1.5, color: colors.textMuted, maxWidth: "560px" }}
        >
          A plan is a saved recipe — four trays, a cadence, and a date range.{" "}
          <strong style={{ color: colors.textBright, fontWeight: 600 }}>APPLY</strong> writes it to
          the calendar as real cycles and scheduled events.
        </div>
      </div>

      {plans.isLoading ? (
        <Skeleton count={3} />
      ) : plans.error ? (
        <ErrorBanner message={`Failed to load plans: ${plans.error.message}`} />
      ) : (plans.data ?? []).length === 0 ? (
        <EmptyState onStart={() => setMode("wizard")} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {(plans.data ?? []).map((p) => (
            <PlanRowCard
              key={p.id}
              plan={p}
              onGenerate={() => {
                setActivePlan(p);
                setMode("wizard");
              }}
              onNavigateToday={onNavigateToday}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState({ onStart }: { onStart: () => void }) {
  return (
    <div
      style={{
        border: `1px dashed ${colors.borderStrong}`,
        padding: "40px 20px",
        textAlign: "center",
        color: colors.textMuted,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "12px",
      }}
    >
      <Sprout width={28} height={28} strokeWidth={1.3} color={colors.textDim} />
      <div style={{ fontSize: "13px", lineHeight: 1.5, maxWidth: "380px" }}>
        No plans yet. The wizard generates a forward cadence across four trays and Tue/Fri delivery
        targets. Save it as a template, then <strong>APPLY</strong> to write cycles and events to
        the calendar.
      </div>
      <button
        type="button"
        onClick={onStart}
        style={{
          marginTop: "4px",
          minHeight: "44px",
          padding: "0 18px",
          background: colors.infrared,
          color: "#fff",
          border: "none",
          fontFamily: typography.mono,
          fontSize: "11px",
          letterSpacing: "0.15em",
          cursor: "pointer",
        }}
      >
        GENERATE YOUR FIRST PLAN
      </button>
    </div>
  );
}

function PlanRowCard({
  plan,
  onGenerate,
  onNavigateToday,
}: {
  plan: PlanRow;
  onGenerate: () => void;
  onNavigateToday: () => void;
}) {
  const qc = useQueryClient();
  const [applyConflict, setApplyConflict] = useState<ApplyConflict | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);

  const del = useMutation({
    mutationFn: () => api.deletePlan(plan.id),
    onSettled: () => void qc.invalidateQueries({ queryKey: ["plans"] }),
  });

  // Saved plans don't persist tray config, so apply re-uses the wizard
  // defaults (matches what most plans were created with). If a plan was
  // built with overrides, the operator should use REGENERATE -> APPLY in
  // the wizard instead.
  const buildBody = (): PlanGenerateBody => ({
    start_date: plan.start_date,
    duration_days: durationDaysBetween(plan.start_date, plan.end_date),
    trays: defaultTrays().map((t) => toTrayInput(t)),
  });

  const apply = useMutation<void, Error, void>({
    mutationFn: async () => {
      setApplyError(null);
      setApplyConflict(null);
      await api.applyPlan(plan.id, buildBody());
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["plans"] });
      void qc.invalidateQueries({ queryKey: ["cycles"] });
      void qc.invalidateQueries({ queryKey: ["events"] });
      onNavigateToday();
    },
    onError: (e) => {
      const conflict = parseApplyConflict(e);
      if (conflict) setApplyConflict(conflict);
      else setApplyError(e.message);
    },
  });

  const deleteAndReapply = useMutation<void, Error, void>({
    mutationFn: async () => {
      setApplyError(null);
      await api.deletePlan(plan.id);
      const fresh = await api.createPlan({
        name: plan.name,
        start_date: plan.start_date,
        end_date: plan.end_date,
      });
      await api.applyPlan(fresh.id, buildBody());
    },
    onSuccess: () => {
      setApplyConflict(null);
      void qc.invalidateQueries({ queryKey: ["plans"] });
      void qc.invalidateQueries({ queryKey: ["cycles"] });
      void qc.invalidateQueries({ queryKey: ["events"] });
      onNavigateToday();
    },
    onError: (e) => {
      const conflict = parseApplyConflict(e);
      if (conflict) setApplyConflict(conflict);
      else setApplyError(e.message);
    },
  });

  const working = apply.isPending || deleteAndReapply.isPending;

  return (
    <div
      style={{
        background: colors.bgRaised,
        border: `1px solid ${colors.border}`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          padding: "14px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "2px", flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: typography.serif,
              fontStyle: "italic",
              fontSize: "18px",
              color: colors.textBright,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {plan.name}
          </div>
          <div style={{ fontSize: "11px", color: colors.textFaint, letterSpacing: "0.1em" }}>
            {plan.start_date} → {plan.end_date}
          </div>
          <div
            style={{
              fontFamily: typography.mono,
              fontSize: "10px",
              color: colors.textDim,
              letterSpacing: "0.18em",
              marginTop: "2px",
            }}
          >
            TEMPLATE · APPLY TO COMMIT TO CALENDAR
          </div>
        </div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={onGenerate}
            aria-label={`Open plan ${plan.name} in wizard`}
            style={{
              minHeight: "44px",
              padding: "0 12px",
              background: "transparent",
              color: colors.textMuted,
              border: `1px solid ${colors.borderStrong}`,
              fontFamily: typography.mono,
              fontSize: "11px",
              letterSpacing: "0.15em",
              cursor: "pointer",
            }}
          >
            OPEN
          </button>
          <button
            type="button"
            onClick={() => apply.mutate()}
            disabled={working}
            style={{
              minHeight: "44px",
              padding: "0 12px",
              background: working ? colors.borderStrong : colors.infrared,
              color: "#fff",
              border: "none",
              fontFamily: typography.mono,
              fontSize: "11px",
              letterSpacing: "0.15em",
              cursor: working ? "not-allowed" : "pointer",
              opacity: working ? 0.55 : 1,
            }}
            aria-label={`Apply plan ${plan.name} to calendar`}
          >
            {working ? "APPLYING…" : "APPLY"}
          </button>
          <ConfirmButton
            onConfirm={() => del.mutate()}
            confirmLabel="CONFIRM DELETE"
            style={{
              background: "transparent",
              border: `1px solid ${colors.borderStrong}`,
              color: colors.textDim,
              fontFamily: typography.mono,
              fontSize: "11px",
              letterSpacing: "0.15em",
              padding: "0 12px",
            }}
            confirmStyle={{
              color: "#fff",
              background: colors.infraredDeep,
              borderColor: colors.infrared,
            }}
            ariaLabel={`Delete plan ${plan.name}`}
          >
            <Trash2 width={14} height={14} strokeWidth={1.5} />
          </ConfirmButton>
        </div>
      </div>
      {applyError && (
        <div style={{ padding: "0 16px 14px 16px" }}>
          <ErrorBanner message={applyError} />
        </div>
      )}
      {applyConflict && (
        <div style={{ padding: "0 16px 14px 16px" }}>
          <ApplyConflictPanel
            conflict={applyConflict}
            onDismiss={() => setApplyConflict(null)}
            onDeleteAndReapply={() => deleteAndReapply.mutate()}
            working={working}
          />
        </div>
      )}
    </div>
  );
}

function durationDaysBetween(startISO: IsoDate, endISO: IsoDate): number {
  const start = new Date(`${startISO}T00:00:00Z`).getTime();
  const end = new Date(`${endISO}T00:00:00Z`).getTime();
  const days = Math.round((end - start) / (24 * 60 * 60 * 1000));
  return Math.max(1, days);
}

function GeneratorWizard({
  existingPlan,
  onExit,
  onNavigateToday,
}: {
  existingPlan: PlanRow | null;
  onExit: () => void;
  onNavigateToday: () => void;
}) {
  const qc = useQueryClient();
  const [step, setStep] = useState<WizardStep>(existingPlan ? "date" : "name");
  const [name, setName] = useState<string>(existingPlan?.name ?? "");
  const [startDate, setStartDate] = useState<IsoDate>(
    existingPlan?.start_date ?? addIsoDays(todayISO(), 1),
  );
  const [durationDays, setDurationDays] = useState<number>(60);
  const [trays, setTrays] = useState<TrayDraft[]>(defaultTrays());
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PlanGenerateResponse | null>(null);
  const [planId, setPlanId] = useState<string | null>(existingPlan?.id ?? null);
  const [applyConflict, setApplyConflict] = useState<ApplyConflict | null>(null);

  const reference = useReference();

  const generate = useMutation<{ plan: PlanRow; response: PlanGenerateResponse }, Error, void>({
    mutationFn: async () => {
      setError(null);
      let plan = existingPlan ?? (planId ? ({ id: planId } as PlanRow) : null);
      if (!plan) {
        plan = await api.createPlan({
          name,
          start_date: startDate,
          end_date: addIsoDays(startDate, durationDays),
        });
      }
      const body: PlanGenerateBody = {
        start_date: startDate,
        duration_days: durationDays,
        trays: trays.map((t) => toTrayInput(t)),
      };
      const response = await api.generatePlan(plan.id, body);
      return { plan, response };
    },
    onSuccess: ({ plan, response }) => {
      setPlanId(plan.id);
      setPreview(response);
      setStep("preview");
      void qc.invalidateQueries({ queryKey: ["plans"] });
    },
    onError: (e) => setError(e.message),
  });

  const apply = useMutation<void, Error, void>({
    mutationFn: async () => {
      setError(null);
      setApplyConflict(null);
      if (!planId) throw new Error("No plan to apply");
      const body: PlanGenerateBody = {
        start_date: startDate,
        duration_days: durationDays,
        trays: trays.map((t) => toTrayInput(t)),
      };
      await api.applyPlan(planId, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["plans"] });
      void qc.invalidateQueries({ queryKey: ["cycles"] });
      void qc.invalidateQueries({ queryKey: ["events"] });
      onNavigateToday();
    },
    onError: (e) => {
      const conflict = parseApplyConflict(e);
      if (conflict) setApplyConflict(conflict);
      else setError(e.message);
    },
  });

  const deleteAndReapply = useMutation<void, Error, void>({
    mutationFn: async () => {
      if (!planId) throw new Error("No plan to delete");
      setError(null);
      await api.deletePlan(planId);
      const fresh = await api.createPlan({
        name: name || existingPlan?.name || "Untitled plan",
        start_date: startDate,
        end_date: addIsoDays(startDate, durationDays),
      });
      setPlanId(fresh.id);
      const body: PlanGenerateBody = {
        start_date: startDate,
        duration_days: durationDays,
        trays: trays.map((t) => toTrayInput(t)),
      };
      await api.applyPlan(fresh.id, body);
    },
    onSuccess: () => {
      setApplyConflict(null);
      void qc.invalidateQueries({ queryKey: ["plans"] });
      void qc.invalidateQueries({ queryKey: ["cycles"] });
      void qc.invalidateQueries({ queryKey: ["events"] });
      onNavigateToday();
    },
    onError: (e) => {
      const conflict = parseApplyConflict(e);
      if (conflict) setApplyConflict(conflict);
      else setError(e.message);
    },
  });

  const headerNav = (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "18px" }}>
      <button
        type="button"
        onClick={onExit}
        style={{
          minHeight: "44px",
          minWidth: "44px",
          background: "transparent",
          border: `1px solid ${colors.borderStrong}`,
          color: colors.textMuted,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        aria-label="Exit wizard"
      >
        <ChevronLeft width={18} height={18} strokeWidth={1.5} />
      </button>
      <StepIndicator current={step} />
    </div>
  );

  return (
    <div>
      {headerNav}
      {error && <ErrorBanner message={error} />}

      {step === "name" && (
        <StepName
          name={name}
          onChange={setName}
          onNext={() => setStep("date")}
          disabled={!name.trim()}
        />
      )}
      {step === "date" && (
        <StepDate
          startDate={startDate}
          durationDays={durationDays}
          onStartChange={setStartDate}
          onDurationChange={setDurationDays}
          onNext={() => setStep("trays")}
          onBack={() => setStep("name")}
        />
      )}
      {step === "trays" && (
        <StepTrays
          trays={trays}
          reference={reference.data ?? []}
          onChange={setTrays}
          onNext={() => generate.mutate()}
          onBack={() => setStep("date")}
          loading={generate.isPending}
        />
      )}
      {step === "preview" && preview && (
        <StepPreview
          response={preview}
          startDate={startDate}
          onBack={() => setStep("trays")}
          onDone={onExit}
          onApply={() => apply.mutate()}
          applying={apply.isPending || deleteAndReapply.isPending}
          applyConflict={applyConflict}
          onDismissConflict={() => setApplyConflict(null)}
          onDeleteAndReapply={() => deleteAndReapply.mutate()}
        />
      )}
    </div>
  );
}

function toTrayInput(t: TrayDraft): PlanTrayInput {
  const raw = t.seed_oz_override?.trim();
  const seedOz = raw ? Number(raw) : null;
  return {
    tray_id: t.tray_id,
    crop: t.crop,
    delivery_target: t.delivery_target,
    tray_fraction: t.tray_fraction,
    seed_oz: seedOz !== null && !Number.isNaN(seedOz) && seedOz > 0 ? seedOz : null,
  };
}

function StepIndicator({ current }: { current: WizardStep }) {
  const items: { id: WizardStep; label: string }[] = [
    { id: "name", label: "NAME" },
    { id: "date", label: "DATE" },
    { id: "trays", label: "TRAYS" },
    { id: "preview", label: "PREVIEW" },
  ];
  return (
    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
      {items.map((it, idx) => {
        const active = current === it.id;
        const done = items.findIndex((x) => x.id === current) > idx;
        return (
          <div
            key={it.id}
            style={{
              padding: "4px 10px",
              fontSize: "10px",
              letterSpacing: "0.18em",
              color: active ? colors.infrared : done ? colors.textMuted : colors.textFaint,
              border: `1px solid ${active ? colors.infrared : colors.border}`,
            }}
          >
            {it.label}
          </div>
        );
      })}
    </div>
  );
}

function StepName({
  name,
  onChange,
  onNext,
  disabled,
}: {
  name: string;
  onChange: (v: string) => void;
  onNext: () => void;
  disabled: boolean;
}) {
  return (
    <WizardCard title="Name your plan">
      <label style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <span style={labelStyle}>Plan name</span>
        <input
          type="text"
          value={name}
          onChange={(e) => onChange(e.target.value)}
          autoFocus
          placeholder="e.g. 60-day Summer 2026"
          style={textInputStyle}
        />
      </label>
      <div style={{ marginTop: "18px" }}>
        <PrimaryButton onClick={onNext} disabled={disabled}>
          NEXT <ArrowRight width={14} height={14} strokeWidth={2} />
        </PrimaryButton>
      </div>
    </WizardCard>
  );
}

function StepDate({
  startDate,
  durationDays,
  onStartChange,
  onDurationChange,
  onNext,
  onBack,
}: {
  startDate: IsoDate;
  durationDays: number;
  onStartChange: (v: IsoDate) => void;
  onDurationChange: (n: number) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  return (
    <WizardCard title="Start date & duration">
      <label style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <span style={labelStyle}>Start date</span>
        <input
          type="date"
          value={startDate}
          onChange={(e) => onStartChange(e.target.value)}
          style={textInputStyle}
        />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "14px" }}>
        <span style={labelStyle}>Duration (days)</span>
        <input
          type="number"
          min={7}
          max={180}
          value={durationDays}
          onChange={(e) => onDurationChange(Number(e.target.value) || 60)}
          style={textInputStyle}
        />
      </label>
      <WizardNav onBack={onBack} onNext={onNext} />
    </WizardCard>
  );
}

function StepTrays({
  trays,
  reference,
  onChange,
  onNext,
  onBack,
  loading,
}: {
  trays: TrayDraft[];
  reference: ReferenceRow[];
  onChange: (t: TrayDraft[]) => void;
  onNext: () => void;
  onBack: () => void;
  loading: boolean;
}) {
  const cropNames = reference.map((r) => r.crop);
  const update = (idx: number, patch: Partial<TrayDraft>) => {
    const next = trays.slice();
    next[idx] = { ...next[idx]!, ...patch };
    onChange(next);
  };
  return (
    <WizardCard title="Configure four trays">
      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        {trays.map((t, idx) => (
          <div
            key={t.tray_id}
            style={{
              padding: "12px",
              background: colors.bgRaised,
              border: `1px solid ${colors.border}`,
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}
            >
              <span
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  background: trayColors[t.tray_id],
                }}
              />
              <div
                style={{ fontFamily: typography.mono, fontSize: "13px", color: colors.textBright }}
              >
                TRAY {t.tray_id}
              </div>
              <div style={{ marginLeft: "auto" }}>
                <TrayIcon
                  size="sm"
                  cycles={[{ id: t.tray_id, crop: t.crop, fraction: t.tray_fraction }]}
                />
              </div>
            </div>
            <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <span style={labelStyle}>Crop</span>
              <select
                value={t.crop}
                onChange={(e) => update(idx, { crop: e.target.value })}
                style={textInputStyle}
              >
                {cropNames.length === 0 && <option value={t.crop}>{t.crop}</option>}
                {cropNames.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <fieldset
              style={{
                display: "flex",
                gap: "10px",
                marginTop: "10px",
                border: "none",
                padding: 0,
              }}
            >
              <legend style={labelStyle}>Delivery target</legend>
              {(["tuesday", "friday"] as const).map((target) => {
                const active = t.delivery_target === target;
                return (
                  <button
                    type="button"
                    key={target}
                    onClick={() => update(idx, { delivery_target: target })}
                    aria-pressed={active}
                    style={{
                      flex: 1,
                      minHeight: "44px",
                      background: active ? colors.bgDeeper : "transparent",
                      color: active ? colors.textBright : colors.textMuted,
                      border: `1px solid ${active ? colors.infrared : colors.border}`,
                      fontFamily: typography.mono,
                      fontSize: "11px",
                      letterSpacing: "0.18em",
                      cursor: "pointer",
                    }}
                  >
                    {target.toUpperCase()}
                  </button>
                );
              })}
            </fieldset>
            <fieldset
              style={{
                display: "flex",
                gap: "6px",
                marginTop: "10px",
                border: "none",
                padding: 0,
                flexWrap: "wrap",
              }}
            >
              <legend style={labelStyle}>Tray share</legend>
              {TRAY_FRACTION_VALUES.map((fraction) => {
                const active = t.tray_fraction === fraction;
                return (
                  <button
                    type="button"
                    key={fraction}
                    onClick={() => update(idx, { tray_fraction: fraction })}
                    aria-pressed={active}
                    style={{
                      flex: 1,
                      minHeight: "40px",
                      padding: "0 6px",
                      background: active ? colors.bgDeeper : "transparent",
                      color: active ? colors.textBright : colors.textMuted,
                      border: `1px solid ${active ? colors.infrared : colors.border}`,
                      fontFamily: typography.mono,
                      fontSize: "12px",
                      letterSpacing: "0.1em",
                      cursor: "pointer",
                    }}
                  >
                    {formatTrayFraction(fraction)}
                  </button>
                );
              })}
            </fieldset>
            <details style={{ marginTop: "10px" }}>
              <summary
                style={{
                  fontSize: "10px",
                  letterSpacing: "0.18em",
                  color: colors.textDim,
                  cursor: "pointer",
                }}
              >
                OVERRIDES
              </summary>
              <div style={{ marginTop: "8px" }}>
                <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  <span style={labelStyle}>seed oz (optional)</span>
                  <input
                    type="number"
                    step="0.25"
                    min={0}
                    value={t.seed_oz_override ?? ""}
                    onChange={(e) => update(idx, { seed_oz_override: e.target.value })}
                    placeholder="auto"
                    style={textInputStyle}
                  />
                </label>
              </div>
            </details>
          </div>
        ))}
      </div>
      <WizardNav
        onBack={onBack}
        onNext={onNext}
        nextLabel={loading ? "GENERATING…" : "PREVIEW"}
        nextDisabled={loading}
      />
    </WizardCard>
  );
}

function StepPreview({
  response,
  startDate,
  onBack,
  onDone,
  onApply,
  applying,
  applyConflict,
  onDismissConflict,
  onDeleteAndReapply,
}: {
  response: PlanGenerateResponse;
  startDate: IsoDate;
  onBack: () => void;
  onDone: () => void;
  onApply: () => void;
  applying: boolean;
  applyConflict: ApplyConflict | null;
  onDismissConflict: () => void;
  onDeleteAndReapply: () => void;
}) {
  const grouped = useMemo(() => {
    const byWeek = new Map<number, RecommendedEvent[]>();
    for (const e of response.recommended_events) {
      const wk = weekBucket(e.date, startDate);
      const arr = byWeek.get(wk);
      if (arr) arr.push(e);
      else byWeek.set(wk, [e]);
    }
    return Array.from(byWeek.entries()).sort(([a], [b]) => a - b);
  }, [response, startDate]);

  return (
    <WizardCard title="Preview">
      {response.warnings.length > 0 && <WarningList warnings={response.warnings} />}
      {applyConflict && (
        <ApplyConflictPanel
          conflict={applyConflict}
          onDismiss={onDismissConflict}
          onDeleteAndReapply={onDeleteAndReapply}
          working={applying}
        />
      )}
      {grouped.length === 0 ? (
        <div style={{ color: colors.textMuted, fontSize: "13px" }}>
          No events generated for this configuration.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
          {grouped.map(([wk, events]) => (
            <div key={wk}>
              <div
                style={{
                  fontSize: "10px",
                  letterSpacing: "0.22em",
                  color: colors.textDim,
                  marginBottom: "8px",
                }}
              >
                WEEK {wk + 1} · {events.length} EVENTS
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {events.map((e, idx) => (
                  <EventLine key={`${e.date}-${e.kind}-${e.tray_id ?? "x"}-${idx}`} event={e} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <div
        style={{
          marginTop: "20px",
          paddingTop: "14px",
          borderTop: `1px solid ${colors.border}`,
          fontSize: "12px",
          lineHeight: 1.55,
          color: colors.textMuted,
        }}
      >
        <strong style={{ color: colors.textBright, fontWeight: 600 }}>SAVE TEMPLATE</strong> stores
        the recipe so you can re-apply or tweak it later — nothing lands on the calendar.{" "}
        <strong style={{ color: colors.textBright, fontWeight: 600 }}>APPLY TO CALENDAR</strong>{" "}
        commits this plan now: real cycles start, and seed/blackout/harvest/delivery events go on
        the schedule.
      </div>
      <div style={{ display: "flex", gap: "10px", marginTop: "14px", flexWrap: "wrap" }}>
        <SecondaryButton onClick={onBack}>EDIT</SecondaryButton>
        <SecondaryButton onClick={onDone}>SAVE TEMPLATE</SecondaryButton>
        <PrimaryButton onClick={onApply} disabled={applying}>
          {applying ? "APPLYING…" : "APPLY TO CALENDAR"}
          {!applying && <ArrowRight width={14} height={14} strokeWidth={2} />}
        </PrimaryButton>
      </div>
    </WizardCard>
  );
}

function ApplyConflictPanel({
  conflict,
  onDismiss,
  onDeleteAndReapply,
  working,
}: {
  conflict: ApplyConflict;
  onDismiss: () => void;
  onDeleteAndReapply: () => void;
  working: boolean;
}) {
  const isAlreadyApplied = conflict.kind === "plan_already_applied";
  const heading = isAlreadyApplied ? "ALREADY APPLIED" : "TRAY CONFLICT";
  return (
    <div
      style={{
        padding: "14px",
        border: `1px solid ${colors.infrared}`,
        background: "rgba(225, 73, 53, 0.06)",
        marginBottom: "18px",
      }}
    >
      <div
        style={{
          fontSize: "11px",
          color: colors.infrared,
          letterSpacing: "0.18em",
          marginBottom: "10px",
        }}
      >
        {heading}
      </div>
      {isAlreadyApplied ? (
        <>
          <div
            style={{ fontSize: "13px", color: colors.text, marginBottom: "6px", lineHeight: 1.5 }}
          >
            This plan already has {conflict.cycles.length} cycle
            {conflict.cycles.length === 1 ? "" : "s"} on the calendar. Re-applying would create
            duplicate cycles and events.
          </div>
          <div
            style={{
              fontSize: "12px",
              color: colors.textMuted,
              marginBottom: "12px",
              lineHeight: 1.5,
            }}
          >
            <strong style={{ color: colors.textBright, fontWeight: 600 }}>DELETE & RE-APPLY</strong>{" "}
            ends the existing run and commits this plan fresh. Skip if the live cycles are still the
            right ones.
          </div>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <ConfirmButton
              onConfirm={onDeleteAndReapply}
              confirmLabel="CONFIRM DELETE & RE-APPLY"
              style={{
                background: "transparent",
                border: `1px solid ${colors.infrared}`,
                color: colors.infrared,
                fontFamily: typography.mono,
                fontSize: "11px",
                letterSpacing: "0.15em",
                padding: "0 12px",
                minHeight: "44px",
              }}
              confirmStyle={{
                color: "#fff",
                background: colors.infraredDeep,
                borderColor: colors.infrared,
              }}
              ariaLabel="Delete existing cycles and re-apply"
            >
              {working ? "WORKING…" : "DELETE & RE-APPLY"}
            </ConfirmButton>
            <SecondaryButton onClick={onDismiss}>DISMISS</SecondaryButton>
          </div>
        </>
      ) : (
        <>
          <div
            style={{ fontSize: "13px", color: colors.text, marginBottom: "8px", lineHeight: 1.5 }}
          >
            {conflict.conflicts.length === 1
              ? "This tray is"
              : `${conflict.conflicts.length} trays are`}{" "}
            busy with a live cycle. End the conflicting cycles on the Trays tab, then APPLY again.
          </div>
          <ul
            style={{
              margin: "0 0 12px 0",
              paddingLeft: "18px",
              color: colors.textMuted,
              fontSize: "12px",
              lineHeight: 1.6,
            }}
          >
            {conflict.conflicts.map((c) => (
              <li key={c.cycle_id} style={{ marginBottom: "4px" }}>
                {c.tray_name ?? `Tray ${c.tray_id}`} · {c.crop}
              </li>
            ))}
          </ul>
          <SecondaryButton onClick={onDismiss}>DISMISS</SecondaryButton>
        </>
      )}
    </div>
  );
}

function EventLine({ event }: { event: RecommendedEvent }) {
  const k = kindStyles[event.kind];
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "8px 10px",
        background: colors.bgRaised,
        border: `1px solid ${colors.border}`,
      }}
    >
      <span
        style={{
          fontFamily: typography.mono,
          fontSize: "10px",
          color: colors.textFaint,
          width: "76px",
          flexShrink: 0,
        }}
      >
        {event.date}
      </span>
      <span
        style={{
          padding: "2px 6px",
          background: k.bg,
          color: k.fg,
          fontSize: "9px",
          letterSpacing: "0.15em",
          border: `1px solid ${k.ring}`,
          minWidth: "54px",
          textAlign: "center",
        }}
      >
        {k.label}
      </span>
      {event.tray_id && (
        <span
          style={{
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: trayColors[event.tray_id] ?? colors.textDim,
            flexShrink: 0,
          }}
          aria-label={`Tray ${event.tray_id}`}
        />
      )}
      <span style={{ fontSize: "12px", color: colors.text, flex: 1, minWidth: 0 }}>
        {event.text}
      </span>
      {event.kind === "harvest" && (
        <span
          style={{
            fontSize: "9px",
            letterSpacing: "0.15em",
            color: colors.infrared,
            padding: "2px 6px",
            border: `1px solid ${colors.infraredDeep}`,
          }}
        >
          DELIVERY
        </span>
      )}
    </div>
  );
}

function WarningList({ warnings }: { warnings: GeneratorWarning[] }) {
  return (
    <div
      style={{
        padding: "12px",
        border: `1px solid ${colors.gold}`,
        background: "rgba(212, 176, 74, 0.08)",
        marginBottom: "18px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontSize: "11px",
          color: colors.gold,
          letterSpacing: "0.18em",
          marginBottom: "8px",
        }}
      >
        <AlertTriangle width={14} height={14} strokeWidth={1.8} />
        SCHEDULE WARNINGS
      </div>
      <ul style={{ margin: 0, paddingLeft: "18px", color: colors.textMuted, fontSize: "12px" }}>
        {warnings.map((w, idx) => (
          <li key={idx} style={{ marginBottom: "4px" }}>
            {w.kind === "aged_seed" ? (
              <>
                Tray {w.tray_id} · {w.crop}: bumped {w.original_seed_oz}oz → {w.bumped_seed_oz}oz (
                {w.reason}).
              </>
            ) : (
              <>
                Tray {w.tray_id} · {w.crop}: first seed lands {w.days_before_start} day
                {w.days_before_start === 1 ? "" : "s"} before plan start ({w.seed_date}). Soak
                earlier or shift the plan start date.
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function WizardCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: "20px 18px",
        background: colors.bgDeeper,
        border: `1px solid ${colors.border}`,
      }}
    >
      <div
        style={{
          fontFamily: typography.serif,
          fontStyle: "italic",
          fontSize: "22px",
          color: colors.textBright,
          marginBottom: "18px",
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function WizardNav({
  onBack,
  onNext,
  nextLabel = "NEXT",
  nextDisabled,
}: {
  onBack?: () => void;
  onNext: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
}) {
  return (
    <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
      {onBack && <SecondaryButton onClick={onBack}>BACK</SecondaryButton>}
      <PrimaryButton onClick={onNext} disabled={nextDisabled}>
        {nextLabel} <ArrowRight width={14} height={14} strokeWidth={2} />
      </PrimaryButton>
    </div>
  );
}

function PrimaryButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        minHeight: "44px",
        padding: "0 18px",
        background: disabled ? colors.borderStrong : colors.infrared,
        color: "#fff",
        border: "none",
        fontFamily: typography.mono,
        fontSize: "11px",
        letterSpacing: "0.18em",
        cursor: disabled ? "not-allowed" : "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {children}
    </button>
  );
}

function SecondaryButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        minHeight: "44px",
        padding: "0 16px",
        background: "transparent",
        color: colors.textMuted,
        border: `1px solid ${colors.borderStrong}`,
        fontFamily: typography.mono,
        fontSize: "11px",
        letterSpacing: "0.18em",
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

const labelStyle = {
  fontSize: "10px",
  letterSpacing: "0.18em",
  color: colors.textDim,
};

const textInputStyle = {
  minHeight: "44px",
  padding: "8px 12px",
  background: colors.bgDeeper,
  border: `1px solid ${colors.border}`,
  color: colors.textBright,
  fontFamily: typography.mono,
  fontSize: "13px",
};
